import { useRef, useEffect, useState, useCallback } from 'react';
import { useTypewriter, Cursor } from 'react-simple-typewriter';
import MagneticWrapper from './MagneticWrapper';

const useCountUp = (target, delay = 1500) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const duration = 1000;
      const startTime = performance.now();
      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(eased * target));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, delay]);

  return count;
};

const Home = () => {
  const heroRef = useRef(null);
  const yearsCount = useCountUp(2, 1500);

  const [roleText] = useTypewriter({
    words: ['Software Engineer', 'Full-Stack Developer'],
    loop: true,
    typeSpeed: 70,
    deleteSpeed: 40,
    delaySpeed: 2200,
  });

  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const x = e.clientX / window.innerWidth - 0.5;
    const y = e.clientY / window.innerHeight - 0.5;
    heroRef.current.style.setProperty('--orb-x', x);
    heroRef.current.style.setProperty('--orb-y', y);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({ top: el.offsetTop - 64, behavior: 'smooth' });
  };

  return (
    <section ref={heroRef} className="hero" id="home" onMouseMove={handleMouseMove}>
      <div className="hero-noise" />
      <div className="hero-orb hero-orb-1" />
      <div className="hero-orb hero-orb-2" />

      <div className="hero-inner">
        <div className="hero-left">
          <div className="hero-eyebrow fade-up d1">
            <span className="hero-eyebrow-dot" />
            Available for opportunities
          </div>
          <h1 className="hero-name fade-up d2">
            Jm<br /><span className="accent-block">Balatico</span>
          </h1>
          <p className="hero-role fade-up d3">
            {roleText}<Cursor cursorStyle="|" cursorColor="var(--accent)" />
          </p>
          <p className="hero-desc fade-up d4">
            Experienced in building modern applications using <strong>SST, TypeScript, Next.js,</strong> and <strong>AWS</strong>,
            with a focus on automation and API integration. I enjoy creating efficient, scalable solutions and
            continuously improving my skills while working on real-world systems. I'm also eager to collaborate
            with teams and contribute to impactful projects in the tech industry.
          </p>
          <div className="hero-actions fade-up d5">
            <MagneticWrapper>
              <button className="btn-primary" onClick={() => scrollTo('projects')}>
                View my work →
              </button>
            </MagneticWrapper>
            <MagneticWrapper>
              <button className="btn-ghost" onClick={() => scrollTo('contact')}>
                Get in touch
              </button>
            </MagneticWrapper>
          </div>
        </div>

        <div className="hero-right fade-up d6">
          <div className="info-cards-grid">
            <div className="info-card">
              <div className="card-label">Status</div>
              <div className="status-row">
                <span className="status-badge">
                  <span className="status-dot" />
                  Open
                </span>
              </div>
              <div className="card-sub" style={{ marginTop: '0.4rem' }}>to collaborate</div>
            </div>
            <div className="info-card">
              <div className="card-label">Location</div>
              <div className="card-value" style={{ fontSize: '1.1rem' }}>Manila</div>
              <div className="card-sub">Philippines · UTC+8</div>
            </div>
          </div>

          <div className="info-cards-grid">
            <div className="info-card">
              <div className="card-label">Experience</div>
              <div className="card-value">{yearsCount} <span className="card-value-unit">yrs</span></div>
              <div className="card-sub">software development</div>
            </div>
            <div className="info-card">
              <div className="card-label">Stack</div>
              <div className="card-value" style={{ fontSize: '1.1rem' }}>Full-Stack</div>
              <div className="card-sub">+ Cloud infra</div>
            </div>
          </div>

          <div className="info-card">
            <div className="card-label">Primary tech</div>
            <div style={{ marginTop: '0.5rem' }}>
              {['TypeScript', 'Next.js', 'React', 'AWS Lambda', 'SST', 'Golang', 'Node.js'].map((t) => (
                <span key={t} className="mini-pill">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;
